import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { adminApi } from "@/lib/admin-api";
import {
  WAITLIST_PREVIEW_SAMPLE,
  applyWaitlistPreviewPlaceholders,
} from "@/lib/waitlistEmailPreview";

function describeError(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Request failed"
  );
}

export default function AdminWaitlistEmail() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [hint, setHint] = useState("");
  const [testTo, setTestTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.waitlistEmail.get();
      const t = data.template;
      setSubject(t.subject || "");
      setHtmlBody(t.htmlBody || "");
      setSenderName(t.senderName || "");
      setSenderEmail(t.senderEmail || "");
      setUpdatedAt(t.updatedAt || null);
      setPlaceholders(data.placeholders || []);
      setHint(data.senderEnvHint || "");
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const preview = useMemo(
    () => applyWaitlistPreviewPlaceholders(subject, htmlBody, WAITLIST_PREVIEW_SAMPLE),
    [subject, htmlBody],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await adminApi.waitlistEmail.save({
        subject,
        htmlBody,
        senderName: senderName.trim() || null,
        senderEmail: senderEmail.trim() || null,
      });
      toast.success(data.message || "Saved.");
      if (data.template?.updatedAt) setUpdatedAt(data.template.updatedAt);
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    const to = testTo.trim();
    if (!to) {
      toast.error("Enter a recipient email for the test send.");
      return;
    }
    setTesting(true);
    try {
      const data = await adminApi.waitlistEmail.test({
        to,
        subject,
        htmlBody,
        senderName: senderName.trim() || null,
        senderEmail: senderEmail.trim() || null,
      });
      toast.success(data.message || "Sent.");
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading template…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
          <Mail className="h-6 w-6 text-primary" />
          Waitlist email
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          HTML is sent to new waitlist signups via Postmark after they are saved. Use
          placeholders in subject and body, then preview and send a test.
        </p>
        {updatedAt && (
          <p className="text-xs text-muted-foreground mt-2">
            Last saved: {new Date(updatedAt).toLocaleString()}
          </p>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Placeholders</CardTitle>
          <CardDescription>
            {placeholders.length
              ? placeholders.join(" · ")
              : "{{email}} · {{variant}} · {{useCase}}"}
            {" — "}
            values are HTML-escaped when sent.
          </CardDescription>
          {hint ? (
            <CardDescription className="text-amber-600/90">{hint}</CardDescription>
          ) : null}
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wl-subject">Subject</Label>
                <Input
                  id="wl-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wl-html">HTML body</Label>
                <Textarea
                  id="wl-html"
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                  className="font-mono text-xs min-h-[280px] leading-relaxed"
                  spellCheck={false}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">From (optional)</CardTitle>
              <CardDescription>
                Overrides <code className="text-xs">POSTMARK_FROM_NAME</code> /{" "}
                <code className="text-xs">POSTMARK_FROM_EMAIL</code> when set. Sender must
                be verified in Postmark.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="wl-from-name">From name</Label>
                <Input
                  id="wl-from-name"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="RADAR"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wl-from-email">From email</Label>
                <Input
                  id="wl-from-email"
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="hello@yourdomain.com"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save template
            </Button>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Test send</CardTitle>
              <CardDescription>
                Sends the current form content (saved or not) to your inbox using sample
                placeholder values: {WAITLIST_PREVIEW_SAMPLE.email} /{" "}
                {WAITLIST_PREVIEW_SAMPLE.variant}.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="wl-test-to">Recipient</Label>
                <Input
                  id="wl-test-to"
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="you@company.com"
                />
              </div>
              <Button variant="secondary" onClick={handleTest} disabled={testing}>
                {testing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send test
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card className="xl:sticky xl:top-20 h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Preview</CardTitle>
            <CardDescription>
              Subject: <span className="text-foreground font-medium">{preview.subject}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-border bg-white overflow-hidden min-h-[400px]">
              <iframe
                title="Email preview"
                className="w-full min-h-[480px] border-0 bg-white"
                sandbox=""
                srcDoc={preview.htmlBody}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
